import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Award, Bell, Calculator, CalendarDays, ChevronRight, CreditCard, Hammer, Hourglass, Map as MapIcon, Network, Send, Settings, Trash2, UserPlus, UserRound, Users } from "lucide-react";

import { api } from "@/lib/api";
import { MonthlyChart } from "@/components/monthly-chart";
import { Brand } from "@/components/brand";
import { Card, ErrorView, IconButton, Loading, Stat } from "@/components/kit";
import { C, eur, STATUSES, STATUS_LABELS, statusColor } from "@/lib/format";

function Alert({ to, icon: Icon, testID, children }) {
  return (
    <Link to={to} data-testid={testID} className="pressable flex items-center gap-3 rounded-[20px] border border-[#F2994A] bg-[#1E1E1E] p-4">
      <Icon className="h-5 w-5 text-[#F2994A]" aria-hidden />
      <span className="flex-1 text-sm text-white">{children}</span>
      <ChevronRight className="h-5 w-5 text-[#9E9E9E]" aria-hidden />
    </Link>
  );
}

function ByStatus({ d }) {
  const max = Math.max(1, ...Object.values(d.by_status));
  return (
    <Card testID="admin-by-status" className="flex flex-col gap-3">
      <h2 className="t-h3">Dossiers par statut</h2>
      {STATUSES.map((s) => (
        <div key={s} className="flex flex-col gap-1">
          <div className="flex justify-between">
            <span className="t-caption">{STATUS_LABELS[s]}</span>
            <span className="t-caption !text-white" data-testid={`admin-by-status-${s}`}>{d.by_status[s]}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-[#2A2A2A]">
            <div className="h-1.5 transition-all duration-700" style={{ width: `${(d.by_status[s] / max) * 100}%`, backgroundColor: statusColor(s) }} />
          </div>
        </div>
      ))}
    </Card>
  );
}

function TopRanking({ rows }) {
  const col = (r) => (r === 1 ? C.brand : r === 2 ? C.silver : r === 3 ? C.bronze : C.onSurface);
  return (
    <Card className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="t-h3">Classement national {new Date().getFullYear()}</h2>
        <Link to="/admin/ranking" data-testid="admin-ranking-link" className="focus-ring rounded text-sm text-[#D4AF37] hover:underline">Tout voir</Link>
      </div>
      {rows.length === 0 ? <p className="t-caption">Aucun agent classé pour le moment.</p> : rows.map((r) => (
        <div key={r.agent_id} className="flex items-center gap-3 py-1">
          <span className="t-h3 w-6" style={{ color: col(r.rank) }}>{r.rank}</span>
          <span className="flex-1 text-sm font-medium text-white">{r.name} <span className="t-caption">{r.agency}</span></span>
          <span className="text-sm text-[#D4AF37]">{r.points} pts</span>
        </div>
      ))}
    </Card>
  );
}

export default function AdminHome() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => api("/admin/dashboard") });
  const d = q.data;
  return (
    <div data-testid="admin-home-screen" className="flex flex-col">
      <div className="sticky top-0 z-20 flex items-center gap-2 border-b border-[#222222] bg-[#121212]/90 px-4 py-3 backdrop-blur-md md:px-8">
        <div className="flex-1 lg:hidden"><Brand compact /></div>
        <p className="t-caption hidden flex-1 tracking-widest lg:block">ESPACE GÉRANT</p>
        <IconButton icon={CalendarDays} label="Planning" testID="admin-planning-button" onClick={() => navigate("/admin/planning")} />
        <IconButton icon={Bell} label="Notifications" testID="admin-notifications-button" badge={d?.unread_notifications} onClick={() => navigate("/notifications")} />
        <IconButton icon={Settings} label="Paramètres" testID="admin-settings-button" onClick={() => navigate("/admin/settings")} />
      </div>
      {q.isLoading ? <Loading /> : q.isError || !d ? <ErrorView message="Impossible de charger les statistiques." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 pb-8 md:p-8">
          <div>
            <p className="t-caption">ESPACE GÉRANT · {d.me.permission === "super" ? "Administrateur" : "Collaborateur"}</p>
            <h1 className="t-h1">Tableau de bord</h1>
          </div>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
            <section className="flex flex-col gap-2 rounded-[20px] border border-[#D4AF37] bg-[#292415] p-6 fade-up">
              <p className="t-label !text-[#F3E5AB]">MONTANT TOTAL À PAYER</p>
              <p data-testid="admin-to-pay" className="t-display !text-[#D4AF37] md:!text-[44px] md:!leading-[52px]">{eur(d.to_pay)}</p>
              <p className="t-caption !text-[#E0E0E0]">Dont {eur(d.commissions_pending_validation)} en attente de validation · {eur(d.referral_to_pay)} de parrainage</p>
              <p className="t-caption">Déjà payé : {eur(d.paid_total)} · Commissions générées : {eur(d.commissions_generated)}</p>
            </section>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Stat testID="admin-stat-agents" icon={Users} label="Agents inscrits" value={d.agents_total} />
              <Stat testID="admin-stat-new-agents" icon={UserPlus} label="Nouveaux (30 j)" value={d.new_agents} />
              <Stat testID="admin-stat-recs" icon={Send} label="Recommandations reçues" value={d.recommendations} />
              <Stat testID="admin-stat-active" icon={Hourglass} label="Prospects actifs" value={d.active_prospects} />
              <Stat testID="admin-stat-signed" icon={Award} label="Contrats signés" value={d.signed} accent={C.success} />
              <Link to="/admin/referrals" data-testid="admin-referrals-link" className="focus-ring rounded-[20px]">
                <Stat testID="admin-stat-filleuls" icon={Network} label="Filleuls · voir qui a parrainé qui" value={d.filleuls} />
              </Link>
              <Link to="/admin-agents?seg=masters" data-testid="admin-masters-link" className="focus-ring rounded-[20px]">
                <Stat testID="admin-stat-masters" icon={Hammer} label="Maîtres d'œuvre actifs" value={d.masters_total ?? 0} />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { to: "/admin/planning", id: "admin-planning-link", icon: CalendarDays, l: "Planning" },
              { to: "/admin/clients", id: "admin-clients-link", icon: UserRound, l: "Clients" },
              { to: "/admin/stats", id: "admin-map-link", icon: MapIcon, l: "Carte" },
            ].map((s) => (
              <Link key={s.id} to={s.to} data-testid={s.id} className="pressable flex min-h-[52px] items-center justify-center gap-2 rounded-[20px] border border-[#333333] bg-[#1E1E1E] px-2">
                <s.icon className="h-5 w-5 text-[#D4AF37]" aria-hidden />
                <span className="text-sm font-medium text-white">{s.l}</span>
              </Link>
            ))}
          </div>
          {d.projects_without_master > 0 && (
            <Alert to="/admin-dossiers" icon={Hammer} testID="admin-alert-no-master">{d.projects_without_master} dossier(s) en cours sans maître d'œuvre</Alert>
          )}
          {(d.pending_deletions > 0 || d.agents_without_rib > 0 || d.agents_without_tiers > 0) && (
            <div className="grid gap-3 md:grid-cols-3">
              {d.pending_deletions > 0 && <Alert to="/admin/settings" icon={Trash2} testID="admin-alert-deletions">{d.pending_deletions} demande(s) de suppression de compte</Alert>}
              {d.agents_without_tiers > 0 && <Alert to="/admin-agents" icon={Calculator} testID="admin-alert-no-tiers">{d.agents_without_tiers} agent(s) sans barème de commission</Alert>}
              {d.agents_without_rib > 0 && <Alert to="/admin-agents" icon={CreditCard} testID="admin-alert-no-rib">{d.agents_without_rib} agent(s) sans RIB</Alert>}
            </div>
          )}
          <MonthlyChart path="/admin/stats/monthly" testID="admin-monthly-chart" />
          <div className="grid gap-4 lg:grid-cols-2">
            <ByStatus d={d} />
            <TopRanking rows={d.ranking} />
          </div>
        </div>
      )}
    </div>
  );
}
